import { useCallback, useEffect, useRef, useState } from "react";

import { AssetManifest, VersionCheckOptions, VersionCheckResult } from "types/version";
import { resolveDeployedMainScriptPath, resolveLoadedMainScriptPath } from "utils/loadedBundle";
import { computeManifestFingerprint } from "utils/versionFingerprint";

const DEFAULT_OPTIONS: VersionCheckOptions = {
  intervalMs: 60_000,
  manifestUrl: "/asset-manifest.json",
};

/**
 * Watch for a newer build than the one this page is running.
 *
 * What counts as "the one this page is running" is the whole of the problem.
 * Seeding it from the first poll — which is what this hook used to do — asks the
 * network what is deployed and then calls that answer "mine", so a visitor who
 * opens the page *after* a deploy and is served a stale shell by the service
 * worker compares the new manifest against itself and is told they are current.
 * That is exactly backwards: the visitor running old code is the one who needs
 * the prompt, and they were the one guaranteed never to see it.
 *
 * So the running build is read from the document (`utils/loadedBundle`) and the
 * deployed build from the manifest. Where the running build cannot be identified
 * — the dev server's unhashed bundle, a test with no script tags — the old
 * first-poll behaviour still stands, which detects a deploy that lands while the
 * tab is open and never invents an update out of nothing.
 *
 * @param options - Poll interval and manifest URL; both default
 * @returns Whether an update is available, plus the two identities compared
 */
export function useVersionWatcher(options?: Partial<VersionCheckOptions>): VersionCheckResult {
  const { intervalMs, manifestUrl } = { ...DEFAULT_OPTIONS, ...options };
  const [currentFingerprint, setCurrentFingerprint] = useState<string | null>(null);
  const [latestFingerprint, setLatestFingerprint] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  // Lazily, once: the document's scripts do not change under us. A ref would
  // re-run the DOM query on every render and keep only the first answer.
  const [loadedMainScript] = useState<string | null>(resolveLoadedMainScriptPath);

  const fetchManifest = useCallback(async (): Promise<AssetManifest | null> => {
    try {
      const res = await fetch(`${manifestUrl}?_=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return null;
      const json = (await res.json()) as AssetManifest;
      return json;
    } catch {
      return null;
    }
  }, [manifestUrl]);

  const check = useCallback(async () => {
    const manifest = await fetchManifest();
    if (!manifest) return;

    const deployedMainScript = resolveDeployedMainScriptPath(manifest.files || {});

    // The precise comparison: what the document loaded against what is served.
    if (loadedMainScript !== null && deployedMainScript !== null) {
      setCurrentFingerprint(loadedMainScript);
      setLatestFingerprint(deployedMainScript);
      return;
    }

    // Fallback for a build that cannot identify itself. Unchanged from before:
    // the first poll defines "current" and a later difference is an update.
    const fingerprint = computeManifestFingerprint(manifest);
    setLatestFingerprint(fingerprint);
    if (currentFingerprint === null) {
      setCurrentFingerprint(fingerprint);
    }
  }, [fetchManifest, currentFingerprint, loadedMainScript]);

  useEffect(() => {
    void check();
    timerRef.current = window.setInterval(() => {
      void check();
    }, intervalMs);
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
    };
  }, [check, intervalMs]);

  return {
    hasUpdate: Boolean(currentFingerprint && latestFingerprint && currentFingerprint !== latestFingerprint),
    currentFingerprint,
    latestFingerprint,
  };
}
